import type { Variation as VariationFields } from "../api/types";
import { variationParts } from "../format";

/** What a listing added to an item's name, beside the name rather than in it.
 *
 *  Worn gear is offered at an enchant level and a weapon with an element and a
 *  special ability — "+4 Crystal Dagger - Critical Damage". The three are
 *  stored in their own columns so the item stays one item: left on the name,
 *  every augmented dagger is its own row of the price table and a search for a
 *  dagger finds none of them. That is the right call for the data and it makes
 *  two offers look identical on the page, which is what this puts back.
 *
 *  Mode pills, deliberately — same caption ink, same rule-coloured hairline,
 *  no hue. DESIGN.md spends rose and olive on WTS and WTB and nothing else,
 *  and these appear on a handful of rows in a table where the reader is
 *  already following a price colour. They are the difference between two rows,
 *  not a third dimension of one.
 *
 *  Renders nothing at all for the ordinary row, which is nearly every row. */
export function Variation(fields: VariationFields) {
  const parts = variationParts(fields);
  if (parts.length === 0) return null;
  return (
    <span className="variation">
      {parts.map((part) => (
        <span key={part} className="pill">{part}</span>
      ))}
    </span>
  );
}

/** The mark on a row whose prices are not the plain item's at all.
 *
 *  Its sibling above says which of an item one *listing* was. This says
 *  something about a whole row of aggregates: nobody has ever listed this item
 *  plain, so the server priced the row from its variants rather than dropping
 *  it — `server/lib/queries.py`'s `_HAS_PLAIN` is the rule and the argument.
 *  Without the mark, 640,000 sits in a BEST BUY column reading as what a plain
 *  Angel Slayer fetches, which is a figure nobody has ever offered.
 *
 *  The same pill, deliberately, and not a new marker: a reader has already
 *  learned that a bordered caption beside an item name means "not quite this
 *  item", and this is the strongest case of exactly that. One word rather than
 *  a level, because the row is not one variant — it is whichever of them the
 *  scan saw, and the item's own page is where they are split apart.
 *
 *  Renders nothing on nearly every row: every consumable and every material has
 *  been listed plain, so there is nothing to stand in for. */
export function VariantPrices({ from }: { from: boolean }) {
  if (!from) return null;
  return (
    <span className="variation">
      <span
        className="pill"
        title="Never listed plain — these prices are a variant's. Open the item for each one's own market."
      >
        variants
      </span>
    </span>
  );
}
